import React from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { LuChevronUp, LuChevronDown, LuCheck } from 'react-icons/lu';
import { elliottTokens as t } from '../tokens';
import { messageBodySx } from './ElliottMessage';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

export type CaseStudySection = { label: string; text: string };

type CaseStudyCardProps = {
  title: string;
  sections: CaseStudySection[];
  read: boolean;
  expanded: boolean;
  onToggle: () => void;
};

// Read indicator: green-filled circle + white check once opened; hollow grey circle when unread.
const ReadCheckbox: React.FC<{ read: boolean }> = ({ read }) =>
  read ? (
    <Box
      sx={{
        position: 'relative',
        width: 20,
        height: 20,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage:
          'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 45%, rgba(255,255,255,0) 100%), linear-gradient(90deg, #16803d 0%, #16803d 100%)',
        boxShadow: 'inset 0px 2px 3px 0px rgba(0,0,0,0.2)'
      }}
    >
      <LuCheck size={12} color={t.textPrimary} strokeWidth={3} />
    </Box>
  ) : (
    <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: `1.5px solid ${t.borderContrast}` }} />
  );

const CaseStudyCard: React.FC<CaseStudyCardProps> = ({ title, sections, read, expanded, onToggle }) => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      px: '16px',
      py: '12px',
      borderRadius: '8px',
      background: 'linear-gradient(180deg, rgba(247,148,32,0.1) 0%, rgba(241,90,38,0.1) 100%)'
    }}
  >
    {/* Header — click to expand/collapse */}
    <Box
      onClick={onToggle}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            lineHeight: '24px',
            color: t.textPrimary,
            whiteSpace: 'nowrap',
            // Phone: a long case-study title wraps instead of running off the card.
            [MOBILE_SX]: { whiteSpace: 'normal', lineHeight: '20px' }
          }}
        >
          {title}
        </Typography>
        <ReadCheckbox read={read} />
      </Box>
      <Box sx={{ display: 'flex', color: t.textSecondary, flexShrink: 0 }}>
        {expanded ? <LuChevronUp size={20} /> : <LuChevronDown size={20} />}
      </Box>
    </Box>

    <Collapse in={expanded} timeout={250} unmountOnExit>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ height: '1px', width: '100%', bgcolor: 'rgba(255,255,255,0.45)' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {sections.map((s) => (
            <Typography key={s.label} sx={messageBodySx}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {s.label}:
              </Box>{' '}
              {s.text}
            </Typography>
          ))}
        </Box>
      </Box>
    </Collapse>
  </Box>
);

export default CaseStudyCard;
